import { h, defineComponent, onMounted, ref, watch } from 'vue';
import DrawablePhoto from '@/_legacy/components/DrawablePhoto/DrawablePhoto.vue';
import { ImageVersions } from '@/services/assets-api/assetsClient';
import { getImageAsDataUrl } from '@/services/assets-api/assetsApiService';

interface ImageInterface {
  image: string | null;
  drawing: string | null;
  image_id: string | null;
  drawing_id: string | null;
  thumbnail?: string | null;
}

interface DrawablePhotoV1OrV2Props {
  image: ImageInterface;
  drawing: Record<string, string>;
  thumbnail: Record<string, string>;
  allowDrawing: boolean;
  title: string;
  removeButtonTitle: string;
  canBeDeleted: boolean;
  photoClass: string;
  showThumbnail: boolean;
  colors?: [];
  width: number;
  height: number;
  loading: boolean;
  small: boolean;
}

export const DrawablePhotoV1OrV2 = defineComponent<DrawablePhotoV1OrV2Props>({
  name: 'DrawablePhotoV1OrV2',
  props: {
    image: {
      type: Object,
      default: null,
    },
    drawing: {
      type: String,
      default: null,
    },
    thumbnail: {
      type: String,
      default: null,
    },
    allowDrawing: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      default: null,
    },
    removeButtonTitle: {
      type: String,
      default: null,
    },
    canBeDeleted: {
      type: Boolean,
      default: true,
    },
    photoClass: {
      type: String,
      default: '',
    },
    showThumbnail: {
      type: Boolean,
      default: true,
    },
    colors: {
      type: Array,
      default: undefined,
    },
    width: {
      type: Number,
      default: null,
    },
    height: {
      type: Number,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    small: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, ctx) {
    const imageObject = ref({ ...props.image });

    const downloadV2Images = async (image: ImageInterface) => {
      const newImage = { ...image };
      if (image.drawing_id && (!image.drawing || !props.drawing)) {
        //load drawing
        newImage.drawing = await getImageAsDataUrl(image.drawing_id);
      }

      if (image.image_id && !image.image) {
        // load image
        newImage.image = await getImageAsDataUrl(image.image_id);
      }

      if (image.image_id && !props.thumbnail) {
        // load thumbnail
        newImage.thumbnail = await getImageAsDataUrl(image.image_id, ImageVersions.THUMB);
      }

      return newImage;
    };

    watch(() => props.image, async value => {
      if (!value) {
        return;
      }
      imageObject.value = await downloadV2Images(value);
    }, { deep: true, immediate: true });

    onMounted(async () => {
      if (!props.image) {
        return;
      }
      imageObject.value = await downloadV2Images(props.image);
    });

    return function render() {
      return h(DrawablePhoto, {
        props: {
          ...props,
          // we got the whole photo object to process, but pass only binaries here
          image: imageObject.value.image,
          drawing: imageObject.value.drawing,
          thumbnail: imageObject.value.thumbnail,
        },
        // @ts-ignore
        on: ctx.listeners,
      });
    };
  },
});
